import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { PermissionsProvider, usePermissions } from "./context/PermissionsContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { setStoredAuthToken, clearStoredAuthToken } from "./utils/authFetchPatch";

// Pages
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import LeadsPage from "./pages/LeadsPage";
import ImportLeadsPage from "./pages/ImportLeadsPage";
import FieldDataEntryPage from "./pages/FieldDataEntryPage";
import EventRegistrationPage from "./pages/EventRegistrationPage";
import EventCheckinPage from "./pages/EventCheckinPage";
import ProductsPage from "./pages/ProductsPage";
import TasksPage from "./pages/TasksPage";
import CommunicationsPage from "./pages/CommunicationsPage";
import CalendarPage from "./pages/CalendarPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import InstallmentsPage from "./pages/InstallmentsPage";
import PaymentsPage from "./pages/PaymentsPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";
import UserRequestsPage from "./pages/UserRequestsPage";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import TagsPage from "./pages/TagsPage";
import AutomationSettingsPage from "./pages/AutomationSettingsPage";
import FunnelDashboardPage from "./pages/FunnelDashboardPage";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import AccessDisabledPage from "./pages/AccessDisabledPage";
import CustomersPage from "./pages/CustomersPage";
import ContractsPage from "./pages/ContractsPage";
import ContractDetailPage from "./pages/ContractDetailPage";
import IntelligencePage from "./pages/IntelligencePage";
import RepDetailPage from "./pages/RepDetailPage";
import CollectionsWorklistPage from "./pages/CollectionsWorklistPage";
import AccountingPage from "./pages/AccountingPage";
import LandingPagesPage from "./pages/LandingPagesPage";
import PublicLandingPage from "./pages/PublicLandingPage";
import PublicFieldDataPage from "./pages/PublicFieldDataPage";
import PublicBookingFormPage from "./pages/PublicBookingFormPage";
import BookingFormsPage from "./pages/BookingFormsPage";
import TemplateBuilderPage from "./pages/TemplateBuilderPage";
import OfficeBookingFormPage from "./pages/OfficeBookingFormPage";

// Layout
import AppLayout from "./components/layout/AppLayout";
import { logger } from './utils/logger';

const RAW_API_URL = process.env.REACT_APP_BACKEND_URL;
// On a custom domain (e.g., qmcrm.com) the build embeds a different host than
// the page is served from. iOS Safari refuses the cross-origin POST during the
// OAuth handoff with "The object can not be cloned." Resolve to same-origin
// when there's a host mismatch so /api/* requests stay first-party. The
// custom-domain proxy already forwards /api/* to the backend.
const API_URL = (() => {
  try {
    const buildHost = new URL(RAW_API_URL).host;
    if (typeof window !== 'undefined' && window.location.host && buildHost !== window.location.host) {
      return window.location.origin;
    }
  } catch { /* fall through to raw value */ }
  return RAW_API_URL;
})();

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [debugLog, setDebugLog] = useState([]);
  const [stuck, setStuck] = useState(false);

  const log = (msg) => {
    // eslint-disable-next-line no-console
    console.log('[AuthDebug]', msg);
    setDebugLog(prev => [...prev, `${new Date().toISOString().slice(11, 19)} · ${msg}`]);
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      log(`Hash present: ${hash ? hash.slice(0, 60) + (hash.length > 60 ? '…' : '') : '(empty)'}`);
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        log(`Session ID detected, len=${sessionId.length}`);
        
        try {
          const postSession = async () => {
            const res = await fetch(`${API_URL}/api/auth/session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sessionId }),
              credentials: 'include'
            });
            log(`POST status: ${res.status}${res.headers.get('cf-ray') ? ' cf-ray=' + res.headers.get('cf-ray') : ''}`);
            return res;
          };

          const RETRY_DELAYS_MS = [1500, 3000, 6000];
          let response = null;
          for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            if (attempt > 0) {
              log(`Server error — retrying in ${RETRY_DELAYS_MS[attempt - 1] / 1000}s (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length + 1})`);
              await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt - 1]));
            }
            log(`POST ${API_URL}/api/auth/session …`);
            try {
              response = await postSession();
            } catch (netErr) {
              log(`POST threw: ${netErr.message || 'network error'}`);
              response = null;
            }
            if (response && response.status < 500) break;
          }
          if (!response) throw new Error('Could not reach the server after several attempts');
          
          if (response.ok) {
            const userData = await response.json();
            log(`User: ${userData.email || '?'} role=${userData.role} status=${userData.status}`);
            log(`session_token in body? ${userData.session_token ? 'YES (' + userData.session_token.length + ' chars)' : 'NO'}`);

            // Persist the token in localStorage as a fallback for mobile
            // browsers that block / strip the cookie. authFetchPatch.js will
            // automatically attach it as Authorization: Bearer on every fetch.
            if (userData.session_token) {
              setStoredAuthToken(userData.session_token);
              log('Saved token to localStorage');
            } else {
              log('⚠️ Backend did not return session_token — backend may not be redeployed');
            }

            // CRITICAL for mobile browsers: the Set-Cookie header is queued to be
            // applied on the next event-loop tick. If we navigate immediately,
            // the protected-route's /auth/me check can fire before the cookie
            // is actually attached to the cookie store and we get a 401 → redirect
            // to /login. Confirm by polling /auth/me for up to 2s before navigating.
            const verifyAuth = async () => {
              for (let i = 0; i < 8; i++) {
                try {
                  const me = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
                  log(`/auth/me poll #${i + 1}: ${me.status}`);
                  if (me.ok) return true;
                } catch (e) {
                  log(`/auth/me poll #${i + 1} threw: ${e.message || 'unknown'}`);
                }
                await new Promise(r => setTimeout(r, 250));
              }
              return false;
            };
            const cookieReady = await verifyAuth();
            if (!cookieReady) {
              log('❌ Auth never confirmed — bouncing to /login');
              setStuck(true);
              return;
            }
            log('✅ Auth confirmed, navigating…');

            // Check user status and redirect accordingly
            if (userData.status === 'pending') {
              navigate('/pending-approval', { state: { user: userData }, replace: true });
            } else if (userData.status === 'disabled') {
              navigate('/access-disabled', { state: { user: userData }, replace: true });
            } else {
              // Role-based landing page: AEs go to dashboard, others to intelligence
              const landingPage = userData.role === 'account_executive' ? '/dashboard' : '/intelligence';
              navigate(landingPage, { state: { user: userData }, replace: true });
            }
          } else {
            const txt = await response.text().catch(() => '');
            log(`POST failed: ${txt.slice(0, 120)}`);
            setStuck(true);
          }
        } catch (error) {
          log(`Exception: ${error.message || 'unknown'}`);
          setStuck(true);
        }
      } else {
        log('No session_id in hash — bouncing to /login');
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center w-full max-w-md">
        {!stuck && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
            <p className="text-slate-600 font-body">Authenticating...</p>
          </>
        )}
        {stuck && (
          <div className="bg-white border border-red-200 rounded-xl p-4 text-left shadow-lg">
            <p className="text-red-700 font-semibold mb-2">Login could not complete</p>
            <p className="text-xs text-slate-600 mb-3">
              Below is what happened. Take a screenshot and share it so I can debug further.
            </p>
            <pre className="text-[10px] bg-slate-900 text-emerald-300 rounded p-2 overflow-auto max-h-72 whitespace-pre-wrap break-all">
{debugLog.join('\n')}
            </pre>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { window.location.href = '/login'; }}
                className="flex-1 h-9 rounded-md bg-slate-900 text-white text-sm"
              >
                Back to Login
              </button>
              <button
                onClick={() => { window.location.reload(); }}
                className="flex-1 h-9 rounded-md border border-slate-300 text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {!stuck && debugLog.length > 0 && (
          <details className="mt-6 text-left">
            <summary className="text-[10px] text-slate-400 cursor-pointer">debug log</summary>
            <pre className="text-[10px] bg-slate-100 rounded p-2 mt-1 overflow-auto max-h-48 whitespace-pre-wrap">
{debugLog.join('\n')}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip check if user data passed from AuthCallback
    if (location.state?.user) {
      const userData = location.state.user;
      setUser(userData);
      setUserStatus(userData.status || 'active');
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      // Retry up to 3 times with a small delay — handles mobile browser
      // cookie-commit timing where the cookie set by /auth/session isn't
      // yet readable on the very first /auth/me fetch.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include'
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setUserStatus(userData.status || 'active');
            setIsAuthenticated(true);
            return;
          }
          // 401/403 — wait a beat and retry, the cookie may still be settling on mobile
          if (attempt < 2) await new Promise(r => setTimeout(r, 300));
        } catch (error) {
          logger.error('Auth check error:', error);
          if (attempt === 2) {
            clearStoredAuthToken();
            setIsAuthenticated(false);
            return;
          }
          await new Promise(r => setTimeout(r, 300));
        }
      }
      // All retries exhausted — token is invalid; clear it before bouncing to /login.
      clearStoredAuthToken();
      setIsAuthenticated(false);
    };

    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-slate-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Handle pending users - show pending page
  if (userStatus === 'pending') {
    return <PendingApprovalPage user={user} onLogout={() => navigate('/login', { replace: true })} />;
  }

  // Handle disabled users - show disabled page
  if (userStatus === 'disabled') {
    return <AccessDisabledPage user={user} onLogout={() => navigate('/login', { replace: true })} />;
  }

  return <PermissionsProvider><AppLayout user={user}>{children}</AppLayout></PermissionsProvider>;
};

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/event-register" element={<EventRegistrationPage />} />
      <Route path="/event-checkin/:token" element={<EventCheckinPage />} />
      <Route path="/field-entry" element={<PublicFieldDataPage />} />
      <Route path="/pending-approval" element={<PendingApprovalPage onLogout={() => navigate('/login', { replace: true })} />} />
      <Route path="/access-disabled" element={<AccessDisabledPage onLogout={() => navigate('/login', { replace: true })} />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><IntelligencePage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><ErrorBoundary><LeadsPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/leads/import" element={<ProtectedRoute><ErrorBoundary><ImportLeadsPage /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/leads/field-data" element={<ProtectedRoute><FieldDataEntryPage /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute><CommunicationsPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/installments" element={<ProtectedRoute><InstallmentsPage /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/users/requests" element={<ProtectedRoute><UserRequestsPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/events/:eventId" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/tags" element={<ProtectedRoute><TagsPage /></ProtectedRoute>} />
      <Route path="/automation-settings" element={<ProtectedRoute><AutomationSettingsPage /></ProtectedRoute>} />
      <Route path="/funnel" element={<ProtectedRoute><FunnelDashboardPage /></ProtectedRoute>} />
      <Route path="/finance/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/finance/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
      <Route path="/finance/contracts/:contractId" element={<ProtectedRoute><ContractDetailPage /></ProtectedRoute>} />
      <Route path="/contracts/:contractId" element={<ProtectedRoute><ContractDetailPage /></ProtectedRoute>} />
      <Route path="/intelligence/rep/:repId" element={<ProtectedRoute><RepDetailPage /></ProtectedRoute>} />
      <Route path="/collections-worklist" element={<ProtectedRoute><CollectionsWorklistPage /></ProtectedRoute>} />
      <Route path="/accounting" element={<ProtectedRoute><AccountingPage /></ProtectedRoute>} />
      <Route path="/landing-pages" element={<ProtectedRoute><LandingPagesPage /></ProtectedRoute>} />
      <Route path="/booking-forms" element={<ProtectedRoute><BookingFormsPage /></ProtectedRoute>} />
      <Route path="/booking-forms/templates/new" element={<ProtectedRoute><TemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/booking-forms/templates/:templateId" element={<ProtectedRoute><TemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/booking-forms/bookings/:bookingId" element={<ProtectedRoute><OfficeBookingFormPage /></ProtectedRoute>} />
      <Route path="/lp/:slug" element={<PublicLandingPage />} />
      <Route path="/booking/:token" element={<PublicBookingFormPage />} />
      <Route path="/" element={<ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>} />
    </Routes>
  );
}

// Role-based redirect component
function RoleBasedRedirect() {
  const { role, loading } = usePermissions();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  // AEs go to dashboard, others go to intelligence
  if (role === 'account_executive') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Navigate to="/intelligence" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
